import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Alert, CircularProgress, InputAdornment, Divider, Switch,
  FormControlLabel, Chip,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import SaveIcon from '@mui/icons-material/Save';
import api from '../../services/api';

interface ConfigCampo {
  value: string;
  label: string;
  type: 'number' | 'boolean';
  min?: number;
  max?: number;
  ajuda?: string;
  editavel: boolean;
}

type ConfigData = Record<string, ConfigCampo>;

const TAXAS = ['CASHOUT_TAXA_BPS', 'CASHOUT_IVA_BPS', 'PAGAME_TAXA_BPS', 'PAGAME_IVA_BPS'];

export default function PaymentLimits() {
  const [config, setConfig] = useState<ConfigData | null>(null);
  const [valores, setValores] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/admin/config')
      .then(r => {
        const data: ConfigData = r.data.data;
        setConfig(data);
        setValores(Object.fromEntries(Object.entries(data).map(([k, c]) => [k, c.value])));
      })
      .catch(() => setError('Erro ao carregar configurações'))
      .finally(() => setLoading(false));
  }, []);

  const definir = (key: string, valor: string) =>
    setValores(v => ({ ...v, [key]: valor }));

  const handleSave = async () => {
    setError('');
    setSuccess(false);
    setSaving(true);
    try {
      // Só se envia o que este utilizador pode mesmo gravar: o servidor recusa
      // o pedido inteiro se lá vier uma chave sem permissão, e perdia-se o resto.
      const payload = Object.fromEntries(
        Object.entries(valores).filter(([k]) => config?.[k]?.editavel),
      );
      await api.put('/admin/config', payload);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.errors?.join(', ') || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

  const maxAmount = valores.MAX_TRANSACTION_AMOUNT_CVE ?? '';
  const amountNum = parseFloat(maxAmount) || 0;
  const topup = config?.WALLET_TOPUP_ACTIVO;
  const topupLigado = valores.WALLET_TOPUP_ACTIVO === 'true';
  const pagame = config?.PAGAME_APP_ATIVO;
  const pagameLigado = valores.PAGAME_APP_ATIVO === 'true';

  const campoBps = (key: string) => {
    const campo = config?.[key];
    if (!campo) return null;
    const bps = parseInt(valores[key] || '0', 10) || 0;
    return (
      <Box key={key} sx={{ mb: 2.5 }}>
        <TextField
          label={campo.label}
          value={valores[key] ?? ''}
          onChange={e => definir(key, e.target.value.replace(/[^0-9]/g, ''))}
          type="number"
          disabled={!campo.editavel}
          inputProps={{ min: campo.min, max: campo.max, step: 50 }}
          InputProps={{ endAdornment: <InputAdornment position="end">bps</InputAdornment> }}
          fullWidth
          helperText={`${(bps / 100).toFixed(2)}%${campo.ajuda ? ` — ${campo.ajuda}` : ''}`}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: 640 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <TuneIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Limites e Taxas</Typography>
          <Typography variant="body2" color="text.secondary">
            Configurações globais das vias de pagamento
          </Typography>
        </Box>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }}>Configurações guardadas com sucesso.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {topup && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Carregamento de carteira
              </Typography>
              <Chip
                size="small"
                label={topupLigado ? 'Activo' : 'Suspenso'}
                color={topupLigado ? 'success' : 'default'}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Interruptor de emergência. Suspenso, todas as rotas de carregamento devolvem 503
              e nenhum valor é cobrado. A omissão é <strong>suspenso</strong>: faltar a
              configuração fecha a via, nunca a abre.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={topupLigado}
                  disabled={!topup.editavel}
                  onChange={e => definir('WALLET_TOPUP_ACTIVO', e.target.checked ? 'true' : 'false')}
                />
              }
              label={topupLigado ? 'Carregamento activo' : 'Carregamento suspenso'}
            />

            {topupLigado && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Com o carregamento activo, só o <strong>numerário ao balcão</strong> conclui.
                O carregamento por cartão devolve 402 até o crédito passar a vir do callback
                da SISP — o valor só é creditado depois de o banco confirmar o pagamento.
              </Alert>
            )}
            {!topup.editavel && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Só um administrador pode mexer neste interruptor.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {pagame && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>PagaMe na app</Typography>
              <Chip size="small" label={pagameLigado ? 'Activo' : 'Inactivo'} color={pagameLigado ? 'success' : 'default'} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Inactivo, o botão PagaMe aparece no cartão da Wallet mas esbatido, e ao tocar diz
              «Brevemente — teremos novidades!». Activo, abre os links de cobrança. A app lê o interruptor ao
              abrir o início (até 1 minuto). A omissão é <strong>inactivo</strong>.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={pagameLigado}
                  disabled={!pagame.editavel}
                  onChange={e => definir('PAGAME_APP_ATIVO', e.target.checked ? 'true' : 'false')}
                />
              }
              label={pagameLigado ? 'PagaMe activo' : 'PagaMe inactivo'}
            />
            {!pagame.editavel && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Só um administrador pode mexer neste interruptor.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      <Card variant="outlined">
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Limite máximo por transacção
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Transacções acima deste valor são rejeitadas antes de serem enviadas para a SISP/Vinti4.
            Define como <strong>0</strong> para desactivar o limite.
          </Typography>

          <TextField
            label="Montante máximo (CVE)"
            value={maxAmount}
            onChange={e => definir('MAX_TRANSACTION_AMOUNT_CVE', e.target.value.replace(/[^0-9]/g, ''))}
            type="number"
            inputProps={{ min: 0, max: 10_000_000, step: 1000 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">CVE</InputAdornment>,
            }}
            fullWidth
            sx={{ mb: 1 }}
          />

          {amountNum > 0 && (
            <Typography variant="caption" color="text.secondary">
              Equivale a aproximadamente{' '}
              <strong>{(amountNum / 110).toFixed(0)} EUR</strong> ou{' '}
              <strong>{(amountNum / 102).toFixed(0)} USD</strong>
            </Typography>
          )}
          {amountNum === 0 && (
            <Typography variant="caption" color="warning.main">
              ⚠ Sem limite — qualquer montante é aceite
            </Typography>
          )}

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Taxas de levantamento e PagaMe
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Em pontos base — 100 bps = 1%. Estes valores cobram dinheiro a clientes reais:
            mudam o que é retido no próximo levantamento e na próxima cobrança PagaMe.
          </Typography>

          {TAXAS.map(campoBps)}

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Alerta de fraude — valor único
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Uma transacção acima deste valor gera um <strong>alerta de fraude (AMOUNT_SPIKE)</strong> por email.
            Subir reduz o ruído de transacções grandes legítimas (bilhetes, carregamentos). Não bloqueia nada — só alerta.
          </Typography>

          <TextField
            label="Alertar acima de (CVE)"
            value={valores.FRAUD_AMOUNT_SPIKE_CVE ?? ''}
            onChange={e => definir('FRAUD_AMOUNT_SPIKE_CVE', e.target.value.replace(/[^0-9]/g, ''))}
            type="number"
            disabled={!config?.FRAUD_AMOUNT_SPIKE_CVE?.editavel}
            inputProps={{ min: 1000, max: 10_000_000, step: 1000 }}
            InputProps={{ endAdornment: <InputAdornment position="end">CVE</InputAdornment> }}
            fullWidth
          />

          <Divider sx={{ my: 2.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'A guardar...' : 'Guardar alterações'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>Contexto:</strong> A 12/07/2026 foram detectadas tentativas de pagamento de 1.000.000$00 e
        2.000.000$00 via ECM anónimo — todas rejeitadas pela SISP. O limite impede que estes valores cheguem
        à gateway de pagamento.
      </Alert>
    </Box>
  );
}
